/**
 * Kit of parts for the SuperSchema design system.
 * Every component renders `ds-` classes from components.css and is safe to
 * use only inside a `.ds` root (see DsRoot).
 */
import {
  type AriaAttributes,
  type ButtonHTMLAttributes,
  type AnchorHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type RefObject,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { Link } from 'react-router-dom'
import {
  AlertCircle,
  ArrowUpDown,
  Check,
  ChevronDown,
  ChevronUp,
  Globe,
  Info,
  Minus,
  Moon,
  Sun,
  X,
} from 'lucide-react'
import { cn } from '@/utils/cn'
import './tokens.css'
import './components.css'

/* ---------------- Theme ---------------- */

export type DsTheme = 'light' | 'dark'
const THEME_KEY = 'ds.theme'

export function useDsTheme(): [DsTheme, (t: DsTheme) => void] {
  const [theme, setThemeState] = useState<DsTheme>(() => {
    if (typeof window === 'undefined') return 'light'
    const stored = window.localStorage.getItem(THEME_KEY)
    if (stored === 'light' || stored === 'dark') return stored
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  })
  const setTheme = (t: DsTheme) => {
    setThemeState(t)
    try {
      window.localStorage.setItem(THEME_KEY, t)
    } catch {
      /* storage unavailable: theme still applies for this render */
    }
  }
  return [theme, setTheme]
}

const ThemeContext = createContext<[DsTheme, (t: DsTheme) => void]>(['light', () => {}])

/** The scope. Everything in the system lives inside one of these. */
export function DsRoot({ children, className }: { children: ReactNode; className?: string }) {
  const value = useDsTheme()
  return (
    <ThemeContext.Provider value={value}>
      {/* `ds-bleedclip` lives here, once, rather than on individual pages. This
          is the only full-width ancestor every design-system page shares, and
          a page's own `<main>` is often itself `.ds-container` (see
          UiKitPage), which has no width left over to clip at: putting the
          class there clips a running panel straight back to the container,
          which is the silent failure the class exists to prevent. The system
          owns components that deliberately bleed to the viewport edge, so
          clipping at the scope root is the correct default for all of it,
          not a per-page workaround a new page can forget. See ground.css for
          why this must never move onto `.ds-container` itself. */}
      <div className={cn('ds', 'ds-bleedclip', className)} data-ds-theme={value[0]} style={{ minHeight: '100vh' }}>
        {children}
      </div>
    </ThemeContext.Provider>
  )
}

export function ThemeSwitch() {
  const [theme, setTheme] = useContext(ThemeContext)
  return (
    <Button
      variant="ghost"
      size="sm"
      icon
      aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
      onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
    >
      {theme === 'dark' ? <Sun /> : <Moon />}
    </Button>
  )
}

/* ---------------- Buttons ---------------- */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'accent' | 'danger' | 'quiet-danger'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonBase {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  icon?: boolean
  className?: string
  children?: ReactNode
}

export type ButtonProps = ButtonBase & ButtonHTMLAttributes<HTMLButtonElement>

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, icon, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn('ds-btn', `ds-btn--${variant}`, size !== 'md' && `ds-btn--${size}`, icon && 'ds-btn--icon', loading && 'ds-btn--loading', className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {children}
    </button>
  )
})

export type ButtonLinkProps = ButtonBase & { to: string } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>

export function ButtonLink({ variant = 'primary', size = 'md', icon, className, children, to, ...rest }: ButtonLinkProps) {
  const cls = cn('ds-btn', `ds-btn--${variant}`, size !== 'md' && `ds-btn--${size}`, icon && 'ds-btn--icon', className)
  if (/^https?:\/\//.test(to)) {
    return (
      <a className={cls} href={to} {...rest}>
        {children}
      </a>
    )
  }
  return (
    <Link className={cls} to={to} {...rest}>
      {children}
    </Link>
  )
}

/* ---------------- Numbers ---------------- */

/**
 * A number, set so a column of them lines up and a comma still looks like a
 * comma. Satoshi's proportional digits differ in width by more than half,
 * which makes any column of them jitter; its tabular digits fix that but give
 * the separators a full digit of width each. This sets the digits tabular and
 * hands the separators back to the proportional set.
 */
export function Fig({ children }: { children: string | number }) {
  const text = typeof children === 'number' ? children.toLocaleString() : children
  return (
    <span className="ds-fig">
      {text.split(/([.,:\/])/).map((part, i) =>
        /^[.,:\/]$/.test(part) ? (
          <span key={i} className="ds-fig__sep">
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </span>
  )
}

/* ---------------- Fields ---------------- */

/**
 * A field that has an error is an invalid field, and the control inside it is
 * the thing a screen reader announces as invalid. Every caller had to say that
 * twice: once as `error` on the Field, once as `aria-invalid` on the control,
 * keyed off the same variable. Seventeen call sites did; every future one was
 * a chance to forget, and forgetting is silent, because `error` still paints
 * the message underneath.
 *
 * So the Field tells the control. `error` is the single source of truth, and a
 * caller who passes `aria-invalid` explicitly still wins.
 */
const FieldContext = createContext<{ invalid: boolean }>({ invalid: false })

/** What a control should render for `aria-invalid`, given what it was passed. */
function useResolvedInvalid(own: AriaAttributes['aria-invalid']) {
  const { invalid } = useContext(FieldContext)
  if (own !== undefined) return own
  return invalid ? true : undefined
}

interface FieldProps {
  label: string
  hint?: string
  error?: string
  /**
   * A small note that belongs beside the label rather than under the control:
   * a character count, a limit, a unit. Under the control it lands between the
   * field and its hint and pushes the hint away from what it describes.
   */
  aside?: ReactNode
  children: (id: string, describedBy: string | undefined) => ReactNode
  className?: string
}

export function Field({ label, hint, error, aside, children, className }: FieldProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errId = error ? `${id}-err` : undefined
  const describedBy = [hintId, errId].filter(Boolean).join(' ') || undefined
  return (
    <div className={cn('ds-field', className)}>
      <div className="ds-field__head">
        <label className="ds-field__label" htmlFor={id}>
          {label}
        </label>
        {aside && <span className="ds-field__aside">{aside}</span>}
      </div>
      <FieldContext.Provider value={{ invalid: Boolean(error) }}>
        {children(id, describedBy)}
      </FieldContext.Provider>
      {hint && !error && (
        <div className="ds-field__hint" id={hintId}>
          {hint}
        </div>
      )}
      {error && (
        <div className="ds-field__error" id={errId} role="alert">
          <AlertCircle aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, 'aria-invalid': ariaInvalid, ...rest },
  ref,
) {
  return <input ref={ref} className={cn('ds-input', className)} aria-invalid={useResolvedInvalid(ariaInvalid)} {...rest} />
})

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea(
  { className, 'aria-invalid': ariaInvalid, ...rest },
  ref,
) {
  return (
    <textarea ref={ref} className={cn('ds-textarea', className)} aria-invalid={useResolvedInvalid(ariaInvalid)} {...rest} />
  )
})

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select(
  { className, children, 'aria-invalid': ariaInvalid, ...rest },
  ref,
) {
  return (
    <select ref={ref} className={cn('ds-select', className)} aria-invalid={useResolvedInvalid(ariaInvalid)} {...rest}>
      {children}
    </select>
  )
})

export function Checkbox({ label, className, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className={cn('ds-check', className)}>
      <input type="checkbox" {...rest} />
      <span>{label}</span>
    </label>
  )
}

export function Radio({ label, className, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className={cn('ds-check', className)}>
      <input type="radio" {...rest} />
      <span>{label}</span>
    </label>
  )
}

/** The label is optional: in a settings row the row already carries it, and
    repeating it beside the switch says the same thing twice. */
/**
 * Pick one of several, where each option needs a line saying what it is.
 *
 * Underneath it is a plain radio group: a fieldset with a legend and one
 * input per option, so a keyboard tabs to the group once and then moves
 * inside it with the arrow keys, and a screen reader is told how many
 * options there are and which one is chosen. The rows are drawn as cards
 * because five options with a sentence each is a lot of unadorned radio, but
 * the control underneath is the ordinary one and behaves the ordinary way.
 *
 * No icon per option. Five picture-marks beside five labels is decoration,
 * and what tells the options apart is what they say.
 */
export function OneOf<T extends string>({
  legend,
  name,
  value,
  onChange,
  options,
  columns,
  hideLegend,
}: {
  legend: string
  name: string
  value: T | ''
  onChange: (v: T) => void
  options: { value: T; label: string; line?: string }[]
  /**
   * How many across where there is room. One column otherwise, and one
   * column always when the options carry a line of description: two
   * sentences side by side in a narrow panel is worse than a list.
   */
  columns?: 2 | 3
  /**
   * Keeps the legend for screen readers and takes it off the screen, for the
   * one case where a heading directly above already asks the question. The
   * group still has to be named: without a legend a screen reader announces
   * a group of radios and never says what they are for.
   */
  hideLegend?: boolean
}) {
  return (
    <fieldset className="ds-oneof">
      <legend className={hideLegend ? 'ds-sr' : 'ds-oneof__legend'}>{legend}</legend>
      <div className={cn('ds-oneof__opts', columns === 2 && 'ds-oneof__opts--two', columns === 3 && 'ds-oneof__opts--three')}>
        {options.map((o) => (
          <label key={o.value} className="ds-oneof__opt" data-on={value === o.value ? 'true' : undefined}>
            <input type="radio" name={name} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)} />
            <span className="ds-oneof__body">
              <span className="ds-oneof__label">{o.label}</span>
              {o.line && <span className="ds-oneof__line">{o.line}</span>}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}

/**
 * Pick any number of several.
 *
 * The same cards as `OneOf`, because to a person these are the same kind of
 * question and only the arithmetic differs. What changes is the control
 * underneath: checkboxes in a group, so a keyboard tabs to each one and a
 * screen reader is told each is on or off rather than that one of them is
 * chosen.
 *
 * It says so on screen too. A row of cards that happens to allow two answers
 * looks exactly like a row that allows one, and somebody who assumes it is
 * one-of will answer once and move on, which loses the half of the answer
 * that said what kind of shop they are. Hence the line under the legend.
 */
export function AnyOf<T extends string>({
  legend,
  hint,
  name,
  values,
  onChange,
  options,
  columns,
  hideLegend,
}: {
  legend: string
  /** Under the legend. Defaults to saying that more than one is allowed. */
  hint?: string
  name: string
  values: T[]
  onChange: (v: T[]) => void
  options: { value: T; label: string; line?: string }[]
  columns?: 2 | 3
  hideLegend?: boolean
}) {
  function toggle(value: T) {
    onChange(values.includes(value) ? values.filter((v) => v !== value) : [...values, value])
  }

  return (
    <fieldset className="ds-oneof ds-oneof--any">
      <legend className={hideLegend ? 'ds-sr' : 'ds-oneof__legend'}>{legend}</legend>
      {!hideLegend && <p className="ds-oneof__hint">{hint ?? 'Choose as many as apply.'}</p>}
      <div className={cn('ds-oneof__opts', columns === 2 && 'ds-oneof__opts--two', columns === 3 && 'ds-oneof__opts--three')}>
        {options.map((o) => (
          <label key={o.value} className="ds-oneof__opt" data-on={values.includes(o.value) ? 'true' : undefined}>
            <input
              type="checkbox"
              name={name}
              value={o.value}
              checked={values.includes(o.value)}
              onChange={() => toggle(o.value)}
            />
            <span className="ds-oneof__body">
              <span className="ds-oneof__label">{o.label}</span>
              {o.line && <span className="ds-oneof__line">{o.line}</span>}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}

export function Switch({ label, className, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label?: ReactNode }) {
  return (
    <label className={cn('ds-switch', className)}>
      <input type="checkbox" role="switch" {...rest} />
      {label && <span>{label}</span>}
    </label>
  )
}

/* ---------------- Address bar ---------------- */

export type AddressState = 'idle' | 'loading' | 'error' | 'done'

interface AddressBarProps {
  value: string
  onChange: (v: string) => void
  onSubmit: (url: string) => void
  state?: AddressState
  error?: string
  size?: 'md' | 'lg'
  buttonLabel?: string
  loadingLabel?: string
  doneLabel?: string
  placeholder?: string
  /**
   * What the field is called for anyone who cannot see the bar. The default
   * belongs to the marketing scan, which asks for a whole site; a surface
   * that asks for one page has to say so.
   */
  label?: string
  /**
   * The mark at the left of the field. A globe by default, because the field
   * takes a website address; the homepage passes HubSpot's mark because its
   * scan is for HubSpot sites only. Must be an svg element itself: the bar's
   * styles address the mark as a direct svg child.
   */
  icon?: ReactNode
}

export function AddressBar({
  value,
  onChange,
  onSubmit,
  state = 'idle',
  error,
  size = 'md',
  buttonLabel = 'Run my free AI Readiness Scan',
  loadingLabel = 'Starting scan',
  doneLabel = 'Scan started',
  placeholder = 'yourcompany.com',
  label = 'Your website address',
  icon = <Globe aria-hidden="true" />,
}: AddressBarProps) {
  const id = useId()
  const errId = `${id}-err`
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit(value.trim())
      }}
      noValidate
    >
      <div className={cn('ds-address', size === 'lg' && 'ds-address--lg')} data-state={state}>
        {icon}
        <input
          id={id}
          className="ds-address__input"
          type="text"
          inputMode="url"
          autoComplete="url"
          spellCheck={false}
          placeholder={placeholder}
          aria-label={label}
          aria-invalid={state === 'error' || undefined}
          aria-describedby={state === 'error' ? errId : undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={state === 'loading'}
        />
        <Button type="submit" loading={state === 'loading'} disabled={state === 'done'} size={size === 'lg' ? 'lg' : 'md'}>
          {state === 'loading' ? loadingLabel : state === 'done' ? doneLabel : buttonLabel}
        </Button>
      </div>
      {state === 'error' && error && (
        <div className="ds-field__error" id={errId} role="alert" style={{ marginTop: 10, paddingLeft: 8 }}>
          <AlertCircle aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
    </form>
  )
}

/* ---------------- Status ---------------- */

export type StatusKind = 'good' | 'look' | 'absent' | 'gone' | 'pending'
export const STATUS_LABEL: Record<StatusKind, string> = {
  good: 'Confirmed',
  look: 'Needs a look',
  absent: 'Not on your page',
  gone: 'Page removed',
  pending: 'Not checked yet',
}
const STATUS_ICON: Record<StatusKind, typeof Check> = {
  good: Check,
  look: AlertCircle,
  absent: Minus,
  gone: X,
  pending: Minus,
}

export function Status({ kind, children, size, className }: { kind: StatusKind; children?: ReactNode; size?: 'sm'; className?: string }) {
  const Icon = STATUS_ICON[kind]
  return (
    <span className={cn('ds-status', `ds-status--${kind}`, size === 'sm' && 'ds-status--sm', className)}>
      <Icon aria-hidden="true" />
      {children ?? STATUS_LABEL[kind]}
    </span>
  )
}

export function Readiness({ good, look, label }: { good: number; look: number; label?: string }) {
  const total = Math.max(1, good + look)
  const pct = (n: number) => `${(n / total) * 100}%`
  return (
    <div>
      <div className="ds-readiness" role="img" aria-label={label ?? `${good} of ${total} pages confirmed, ${look} need a look`}>
        {good > 0 && <span className="ds-readiness__good" style={{ width: pct(good) }} />}
        {look > 0 && <span className="ds-readiness__look" style={{ width: pct(look) }} />}
      </div>
      <div className="ds-readiness-legend" aria-hidden="true">
        <span>
          <b>{good}</b> confirmed
        </span>
        {look > 0 && (
          <span>
            <b>{look}</b> need a look
          </span>
        )}
      </div>
    </div>
  )
}

/** The "know it worked" card. */
export function Proof({ kind = 'good', title, detail }: { kind?: 'good' | 'look' | 'quiet'; title: string; detail?: ReactNode }) {
  const Icon = kind === 'good' ? Check : kind === 'look' ? AlertCircle : Minus
  return (
    <div className={cn('ds-proof', kind !== 'good' && `ds-proof--${kind}`)} role="status">
      <span className="ds-proof__icon" aria-hidden="true">
        <Icon />
      </span>
      <span>
        <div className="ds-proof__title">{title}</div>
        {detail && <div className="ds-proof__detail">{detail}</div>}
      </span>
    </div>
  )
}

/* ---------------- Cards and segmented control ---------------- */

export function Card({
  title,
  head,
  children,
  foot,
  className,
  flat,
  xl,
  flush,
}: {
  title?: string
  head?: ReactNode
  children: ReactNode
  foot?: ReactNode
  className?: string
  flat?: boolean
  xl?: boolean
  flush?: boolean
}) {
  return (
    <section className={cn('ds-card', flat && 'ds-card--flat', xl && 'ds-card--xl', className)}>
      {(title || head) && (
        <div className="ds-card__head">
          {title && <span className="ds-card__title">{title}</span>}
          {head}
        </div>
      )}
      <div className={cn(!flush && 'ds-card__body')}>{children}</div>
      {foot && <div className="ds-card__foot">{foot}</div>}
    </section>
  )
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { id: T; label: string; count?: number }[]
  value: T
  onChange: (id: T) => void
  label?: string
}) {
  return (
    <div className="ds-segmented" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button key={o.id} role="tab" type="button" className="ds-segment" aria-selected={value === o.id} onClick={() => onChange(o.id)}>
          {o.label}
          {typeof o.count === 'number' && <span className="ds-count">{o.count}</span>}
        </button>
      ))}
    </div>
  )
}

/* ---------------- Tables ---------------- */

export interface Column<T> {
  key: string
  header: string
  render: (row: T) => ReactNode
  align?: 'left' | 'right'
  sortable?: boolean
  width?: string | number
  /** Stacked mode: render label above the cell instead of beside it. */
  wide?: boolean
}

export function Table<T>({
  rows,
  columns,
  rowKey,
  empty,
  sort,
  onSort,
  selectedKey,
  pageSize,
  stack,
}: {
  rows: T[]
  columns: Column<T>[]
  rowKey: (row: T) => string
  empty?: ReactNode
  sort?: { key: string; dir: 'asc' | 'desc' }
  onSort?: (key: string) => void
  selectedKey?: string
  pageSize?: number
  stack?: boolean
}) {
  const [page, setPage] = useState(0)
  const size = pageSize ?? rows.length
  const pages = Math.max(1, Math.ceil(rows.length / size))
  const safePage = Math.min(page, pages - 1)
  const visible = rows.slice(safePage * size, safePage * size + size)
  return (
    <div className="ds-table-wrap">
      <table className={cn('ds-table', stack && 'ds-table--stack')}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                style={{ width: c.width, textAlign: c.align }}
                aria-sort={sort?.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
              >
                {c.sortable && onSort ? (
                  <button type="button" onClick={() => onSort(c.key)}>
                    {c.header}
                    {sort?.key === c.key ? sort.dir === 'asc' ? <ChevronUp /> : <ChevronDown /> : <ArrowUpDown />}
                  </button>
                ) : (
                  c.header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="ds-table__empty">
                {empty ?? 'Nothing here yet.'}
              </td>
            </tr>
          ) : (
            visible.map((row) => {
              const k = rowKey(row)
              return (
                <tr key={k} aria-selected={selectedKey === k || undefined}>
                  {columns.map((c) => {
                    const cell = c.render(row)
                    /*
                      A right-aligned column is a column of figures, so it is
                      set tabular, and Satoshi's tabular comma is a full digit
                      wide: measured at 10.08px against 4.16px proportional.
                      Left alone, "1,353" came out as "1 , 353" in every
                      right-aligned column in the system. Fig sets the digits
                      tabular and hands the separators back, which is what it
                      was built for. Anything that is already an element is
                      passed through untouched.
                    */
                    const figured =
                      c.align === 'right' && (typeof cell === 'string' || typeof cell === 'number') ? <Fig>{cell}</Fig> : cell
                    return (
                      <td key={c.key} className={cn(c.align === 'right' && 'ds-num', c.wide && 'ds-cell--wide')} style={{ textAlign: c.align }} data-label={c.header}>
                        {figured}
                      </td>
                    )
                  })}
                </tr>
              )
            })
          )}
        </tbody>
      </table>
      {pageSize && rows.length > pageSize && (
        <div className="ds-pagination">
          <span>
            {safePage * size + 1} to {Math.min(rows.length, (safePage + 1) * size)} of {rows.length}
          </span>
          <div className="ds-pagination__pages">
            <Button variant="ghost" size="sm" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>
              Previous
            </Button>
            <Button variant="ghost" size="sm" disabled={safePage >= pages - 1} onClick={() => setPage(safePage + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

/* ---------------- Menu, dialog, tooltip ---------------- */

export function Menu({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('ds-menu', className)} role="menu">
      {children}
    </div>
  )
}
/*
  `className` is pulled out and merged rather than left in `rest`. Spread last,
  a caller passing one replaced `ds-menu__item` outright and the item lost its
  height, padding and hover along with it.
*/
export function MenuItem({
  children,
  icon,
  shortcut,
  trailing,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  icon?: ReactNode
  shortcut?: string
  /**
   * A mark at the far end of the row: the tick on the choice in force, a
   * count, a chevron.
   *
   * It is its own slot rather than something a caller appends to `children`,
   * because the label is wrapped in a span that shrinks to the text. Anything
   * put inside that span sits after the words and wraps under them the moment
   * there is no room, which is where the theme switch's tick ended up: below
   * its label rather than opposite it, with `margin-left: auto` unable to
   * help because it was not a child of the flex row.
   */
  trailing?: ReactNode
}) {
  return (
    <button type="button" role="menuitem" className={cn('ds-menu__item', className)} {...rest}>
      {icon}
      <span>{children}</span>
      {trailing}
      {shortcut && <kbd>{shortcut}</kbd>}
    </button>
  )
}
export function MenuSeparator() {
  return <hr className="ds-menu__sep" />
}

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl'

/**
 * The panel itself, without the backdrop. Exported so the kit can show every
 * dialog shape side by side without opening them one at a time.
 */
export function DialogCard({
  title,
  lead,
  children,
  actions,
  onClose,
  size = 'md',
  titleId,
  inline,
  innerRef,
}: {
  title: string
  lead?: ReactNode
  children?: ReactNode
  actions: ReactNode
  onClose?: () => void
  size?: DialogSize
  titleId?: string
  inline?: boolean
  innerRef?: RefObject<HTMLDivElement>
}) {
  return (
    <div
      className={cn('ds-dialog', size !== 'md' && `ds-dialog--${size}`, inline && 'ds-dialog--inline')}
      role={inline ? undefined : 'dialog'}
      aria-modal={inline ? undefined : true}
      aria-labelledby={titleId}
      ref={innerRef}
    >
      <div className="ds-dialog__body">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="ds-h3" id={titleId}>
              {title}
            </h2>
            {lead && <p className="ds-dialog__lead">{lead}</p>}
          </div>
          {onClose && (
            <Button variant="ghost" size="sm" icon aria-label="Close" onClick={onClose}>
              <X />
            </Button>
          )}
        </div>
        {children && <div style={{ marginTop: 18 }}>{children}</div>}
      </div>
      <div className="ds-dialog__actions">{actions}</div>
    </div>
  )
}

/**
 * Everything that interrupts a person uses this: confirmations, short forms,
 * explanations, and "not on your plan". Escape closes it, the backdrop closes
 * it, focus is trapped inside while it is open and returns where it came from,
 * and the page behind it cannot scroll.
 */
export function Dialog({
  open,
  onClose,
  title,
  lead,
  children,
  actions,
  size = 'md',
}: {
  open: boolean
  onClose: () => void
  title: string
  lead?: ReactNode
  children?: ReactNode
  actions: ReactNode
  size?: DialogSize
}) {
  const ref = useRef<HTMLDivElement>(null)
  const titleId = useId()

  /**
   * The effect below must depend on `open` alone.
   *
   * Callers pass `onClose` as an inline arrow, so it is a new function on
   * every render. With it in the dependency list the whole effect tore down
   * and set up again on every keystroke, and setting up moves focus to the
   * first focusable element: typing into a field inside a dialog put one
   * character in and then jumped to the close button. Holding it in a ref
   * keeps the handler current without making the effect re-run.
   */
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return
    const prev = document.activeElement as HTMLElement | null
    const panel = ref.current
    const focusable = () =>
      Array.from(
        panel?.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])') ??
          [],
      ).filter((el) => el.offsetParent !== null)

    focusable()[0]?.focus()

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current()
        return
      }
      /* Tab must not walk out of the dialog and onto the page behind it. */
      if (e.key !== 'Tab') return
      const items = focusable()
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
      prev?.focus?.()
    }
  }, [open])

  if (!open) return null
  return (
    <div className="ds-dialog-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <DialogCard title={title} lead={lead} actions={actions} onClose={onClose} size={size} titleId={titleId} innerRef={ref}>
        {children}
      </DialogCard>
    </div>
  )
}

export function Tooltip({ tip, children }: { tip: string; children: ReactNode }) {
  return (
    <span className="ds-tooltip">
      {children}
      <span className="ds-tooltip__tip" role="tooltip">
        {tip}
      </span>
    </span>
  )
}

/* ---------------- Notice, empty, skeleton, kbd ---------------- */

export function Notice({ kind = 'info', children }: { kind?: 'info' | 'look' | 'good'; children: ReactNode }) {
  const Icon = kind === 'look' ? AlertCircle : kind === 'good' ? Check : Info
  return (
    <div className={cn('ds-notice', kind !== 'info' && `ds-notice--${kind}`)} role={kind === 'look' ? 'alert' : undefined}>
      <Icon aria-hidden="true" />
      <div>{children}</div>
    </div>
  )
}

export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="ds-empty">
      <h3 className="ds-h4">{title}</h3>
      {children && <p>{children}</p>}
      {action && <div style={{ marginTop: 8 }}>{action}</div>}
    </div>
  )
}

/**
 * The pill radius is right for a line of text and wrong for a block: a
 * 340px-tall placeholder drawn at the pill radius is a giant oval. Anything
 * taller than a line gets the card radius instead.
 */
export function Skeleton({ width = '100%', height = 14, className }: { width?: string | number; height?: number; className?: string }) {
  const radius = height > 28 ? 'var(--ds-radius)' : 'var(--ds-radius-pill)'
  return <span className={cn('ds-skeleton', className)} style={{ width, height, borderRadius: radius }} aria-hidden="true" />
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="ds-kbd">{children}</kbd>
}

/* ---------------- Steps ---------------- */

/*
  In a wizard a step is done because it is behind you, so the position says
  everything and `done` can be left out. Where steps can be finished in any
  order, position is not the truth: pass `done` per step, or a bar goes blue
  for something nobody has done yet.
*/
export function Steps({ steps, current, done }: { steps: string[]; current: number; done?: boolean[] }) {
  return (
    <ol className="ds-steps" aria-label="Progress">
      {steps.map((s, i) => (
        <li
          key={s}
          className="ds-step"
          aria-current={i === current ? 'step' : undefined}
          data-done={(done ? done[i] : i < current) || undefined}
        >
          {s}
        </li>
      ))}
    </ol>
  )
}

/* ---------------- Logos ---------------- */

/**
 * The brand mark.
 *
 * A supplied object, not something the interface draws: it keeps its own
 * blue and its own shading in both themes, which is exactly what the rule
 * about the mark asks for. The one it replaced was drawn here as SVG paths
 * filled with `fill-primary`, so the mark changed colour whenever the theme
 * did, which is the one thing a mark must never do.
 *
 * Decorative on purpose. Everywhere it appears it is inside a link that
 * already says "SuperSchema", so a second reading of the name is noise.
 */
export function BrandMark({ className, size = 28 }: { className?: string; size?: number }) {
  return (
    <img
      className={cn('ds-brandmark', className)}
      src="/design-system/brand/super-schema-mark.png"
      alt=""
      aria-hidden="true"
      width={size}
      height={size}
      decoding="async"
    />
  )
}

export const CUSTOMER_LOGOS = [
  { src: '/customers/PatientFi.png', alt: 'PatientFi' },
  { src: '/customers/dalim.png', alt: 'Dalim' },
  { src: '/customers/elisa.png', alt: 'Elisa' },
  { src: '/customers/ezeep.png', alt: 'ezeep' },
  { src: '/customers/majortom.png', alt: 'Major Tom' },
  { src: '/customers/playon.png', alt: 'PlayOn' },
  { src: '/customers/ymca.png', alt: 'YMCA' },
]

export function LogoRow() {
  return (
    <div className="ds-logos">
      {CUSTOMER_LOGOS.map((l) => (
        <img key={l.alt} src={l.src} alt={l.alt} loading="lazy" />
      ))}
    </div>
  )
}
